import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  BarChart3,
  BookOpen,
  Briefcase,
  Building,
  CalendarDays,
  Check,
  Coins,
  Download,
  Gift,
  Home,
  FileText,
  Handshake,
  KeyRound,
  Landmark,
  Layers,
  LayoutDashboard,
  Lock,
  LogOut,
  Menu,
  Moon,
  Package,
  Palette,
  Receipt,
  Settings,
  Shield,
  Sliders,
  Smartphone,
  Store,
  Sun,
  Sparkles,
  TrendingUp,
  Truck,
  Upload,
  Star,
  UserPlus,
  Users,
  Users2,
  UserCog,
  UtensilsCrossed,
  Ticket,
  Trophy,
  Wallet,
  Wifi,
  X,
  CalendarClock,
  MessageSquare,
  ShieldOff,
  Search,
  ClipboardCheck,
  ClipboardList,
  Boxes,
} from 'lucide-react'
import { getAccessFlags, getAllUnitsAccessFlags, canUseAllUnitsMode } from '@/lib/access'
import { apiFetch } from '@/utils/api'
import { businessDayLocalISO, toISODate, todayLocalISO } from '@/utils/date'
import { useAuthStore } from '@/stores/authStore'
import { useUnitStore } from '@/stores/unitStore'
import { useVersionStore } from '@/stores/versionStore'
import { useThemeStore } from '@/stores/themeStore'
import { themes, THEME_LABELS } from '@/themes'
import { NavGroup } from '@/components/NavGroup'
import { MobileBottomNav } from '@/components/MobileBottomNav'
import { InstallPrompt } from '@/components/InstallPrompt'

function navLinkClass({ isActive }: { isActive: boolean }) {
  return `flex items-center gap-3 rounded-lg px-3 py-2 text-zinc-200 hover:bg-white/5 ${isActive ? 'bg-white/10 text-white' : ''}`
}

export function AppShell() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useThemeStore((s) => s.theme)
  const toggleTheme = useThemeStore((s) => s.toggleTheme)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false)
  const [navSearch, setNavSearch] = useState('')
  const unitDropdownRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const mobileNavRef = useRef<HTMLElement>(null)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const units = useUnitStore((s) => s.units)
  const appVersion = useVersionStore((s) => s.info.semver)
  const loadVersion = useVersionStore((s) => s.load)
  useEffect(() => { void loadVersion() }, [loadVersion])
  const selectedUnitId = useUnitStore((s) => s.selectedUnitId)
  const selectUnit = useUnitStore((s) => s.selectUnit)
  const loadUnits = useUnitStore((s) => s.loadUnits)
  const unitsLoaded = useUnitStore((s) => s.unitsLoaded)
  const rolesByUnit = useUnitStore((s) => s.rolesByUnit)
  const loadRoles = useUnitStore((s) => s.loadRoles)

  useEffect(() => {
    void loadUnits()
  }, [loadUnits])

  // "Todas as unidades" (selectedUnitId null): carrega as permissões de cada
  // unidade. Antes nada era carregado nesse modo: o menu sumia e as telas
  // ficavam em branco.
  useEffect(() => {
    if (selectedUnitId) {
      void loadRoles(selectedUnitId)
      return
    }
    if (!unitsLoaded) return
    for (const u of units) void loadRoles(u.id).catch(() => undefined)
  }, [selectedUnitId, loadRoles, unitsLoaded, units])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target as Node)) {
        setUnitDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    // Smooth scroll to top when route changes for better UX
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' as ScrollBehavior })
    // Reset nav search when navigating
    setNavSearch('')
  }, [location.pathname])

  useEffect(() => {
    const query = navSearch.trim().toLowerCase()
    const containers = [navRef.current, mobileNavRef.current].filter((el): el is HTMLElement => Boolean(el))
    for (const container of containers) {
      const links = container.querySelectorAll<HTMLAnchorElement>('a[href]')
      links.forEach((link) => {
        if (!query) {
          link.style.removeProperty('display')
          return
        }
        const text = (link.textContent ?? '').toLowerCase()
        link.style.display = text.includes(query) ? '' : 'none'
      })
      const groups = container.querySelectorAll<HTMLElement>('[data-nav-group]')
      groups.forEach((group) => {
        if (!query) {
          group.style.removeProperty('display')
          return
        }
        const visibleLinks = Array.from(group.querySelectorAll<HTMLAnchorElement>('a[href]'))
          .some((link) => link.style.display !== 'none')
        group.style.display = visibleLinks ? '' : 'none'
      })
    }
  }, [navSearch, location.pathname])

  // Enter = Tab: operadoras estão acostumadas a dar Enter a cada campo. Aqui o
  // Enter num <input> pula pro PRÓXIMO campo de formulário (input/select/textarea),
  // o que dispara o salvamento on-blur do campo atual. No último campo o Enter
  // segue o comportamento padrão (preserva "Enter envia" de modais/busca).
  // Opt-out por campo: atributo data-enter-default. Shift/Ctrl/Alt e textarea
  // (quebra de linha) não são afetados.
  useEffect(() => {
    function handleEnterAsTab(e: KeyboardEvent) {
      if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return
      const target = e.target as HTMLElement | null
      if (!target || target.tagName !== 'INPUT') return
      const input = target as HTMLInputElement
      const skip = ['checkbox', 'radio', 'submit', 'button', 'reset', 'file', 'range', 'color']
      if (skip.includes((input.getAttribute('type') || 'text').toLowerCase())) return
      if (input.dataset.enterDefault !== undefined) return
      const fields = Array.from(
        document.querySelectorAll<HTMLElement>('input, select, textarea'),
      ).filter((el) => {
        const f = el as HTMLInputElement
        if (f.disabled || f.readOnly || el.tabIndex === -1) return false
        const ty = (f.getAttribute('type') || 'text').toLowerCase()
        if (['hidden', 'checkbox', 'radio', 'submit', 'button', 'reset', 'file', 'range', 'color'].includes(ty)) return false
        return el.offsetParent !== null
      })
      const idx = fields.indexOf(input)
      if (idx === -1) return
      const next = fields[idx + 1]
      if (!next) return // último campo: deixa o Enter padrão (preserva submit de modal)
      e.preventDefault()
      next.focus()
      const nextInput = next as HTMLInputElement
      if (typeof nextInput.select === 'function' && nextInput.value != null) {
        try { nextInput.select() } catch { /* alguns tipos não permitem select */ }
      }
    }
    document.addEventListener('keydown', handleEnterAsTab)
    return () => document.removeEventListener('keydown', handleEnterAsTab)
  }, [])

  const selectedUnit = useMemo(() => units.find((unit) => unit.id === selectedUnitId) ?? null, [units, selectedUnitId])
  const isAllUnits = selectedUnitId === null
  const roles = selectedUnitId ? rolesByUnit[selectedUnitId] ?? [] : []
  const access = isAllUnits ? getAllUnitsAccessFlags(rolesByUnit) : getAccessFlags(roles)
  const canSwitchToAllUnits = canUseAllUnitsMode(rolesByUnit) && units.length > 1
  const activeCount = units.filter((u) => u.active ?? true).length
  // Show system admin UI when: admin in selected unit, OR admin in any unit, OR no units exist yet (bootstrap state)
  const hasAdminInAnyUnit = Object.values(rolesByUnit).some(rs => rs.includes('owner') || rs.includes('admin_rede'))
  const canUseSystemAdmin = access.canUseSystemAdmin || hasAdminInAnyUnit || (unitsLoaded && units.length === 0)
  const vouchersEnabled = user?.modules?.includes('vouchers') ?? false
  const wifiEnabled = user?.modules?.includes('wifi') ?? false
  // Dia do Colaborador (módulo nasce desligado; liga primeiro na unidade de QA)
  const diaEnabled = user?.modules?.includes('dia_colaborador') ?? false
  // Bottom nav do colaborador só entra em cena com o módulo ligado (sem mudar o que já existe).
  const showEmployeeBottomNav = diaEnabled && access.isEmployeeOnly && !user?.isMotoboy
  // APK Flutter congelado (29/10/2026, migration 181): o PWA é o app oficial.
  // Menu "Aplicativo (download)" e /app/aplicativo só com o módulo ligado.
  const apkEnabled = user?.modules?.includes('apk_android') ?? false
  const manualEnabled = false
  // Pós-venda de cancelamentos: owner/admin_rede ou permissão postsale.cancellations.review.
  const [canReviewCancellations, setCanReviewCancellations] = useState(false)
  useEffect(() => {
    if (!user) return
    apiFetch<{ canReview: boolean }>('/api/postsale/cancellations/access')
      .then((r) => setCanReviewCancellations(Boolean(r.canReview)))
      .catch(() => setCanReviewCancellations(false))
  }, [user])

  const header = useMemo(() => {
    const path = location.pathname
    if (path.startsWith('/app/aplicativo')) return { title: 'Aplicativo 4 Queijos', subtitle: 'Download · QR code · Instalação · Changelog' }
    if (path.startsWith('/app/mobile')) return { title: 'Operação mobile', subtitle: 'Colaborador · Motoboy' }
    if (path.startsWith('/app/inicio')) return { title: 'Início', subtitle: `Resumo da rede · v${appVersion}` }
    if (path.startsWith('/app/calendario-trabalho')) return { title: 'Calendário de trabalho', subtitle: `Quem trabalha em cada dia · v${appVersion}` }
    if (path.startsWith('/app/my-schedule')) return { title: 'Minha escala', subtitle: `Folgas, trocas e pedidos · v${appVersion}` }
    if (path.startsWith('/app/dashboard')) return { title: 'Dashboard', subtitle: `Visão geral · v${appVersion}` }
    if (path.startsWith('/app/weekly')) return { title: 'Visão da semana', subtitle: 'Semana atual' }
    if (path.match(/\/app\/closing\/[^/]+\/grid/)) return { title: 'Fechamento · Grade', subtitle: 'Visão compacta estilo planilha' }
    if (path.match(/\/app\/closing\/view\//)) return { title: 'Visão Consolidada', subtitle: 'Resumo completo do dia' }
    if (path.startsWith('/app/closing-pro')) return { title: 'Fechamento do gerente', subtitle: 'Vendas · Recebimentos · Despesas · Vales · Motoboys' }
    if (path.startsWith('/app/sector')) return { title: 'Fechamento do gerente', subtitle: 'Vendas · Recebimentos · Despesas · Vales · Motoboys' }
    if (path.startsWith('/app/my-panel')) return { title: 'Meu Painel', subtitle: 'Confirmações e solicitações do colaborador' }
    if (path.startsWith('/app/daily-panel')) return { title: 'Painel do dia', subtitle: 'Completude dos fechamentos por dia' }
    if (path.startsWith('/app/closing/monthly')) return { title: 'Fechamento Mensal', subtitle: 'Calendário comercial 5-4-4 · DRE da unidade' }
    if (path.startsWith('/app/calendar') || path.startsWith('/app/closing')) return { title: 'Fechamento diário', subtitle: `Módulo de operação · v${appVersion}` }
    if (path.startsWith('/app/cardapio/import')) return { title: 'Importar cardápio do PDV', subtitle: 'Sincronizar preços via PDF exportado pelo BitBar' }
    if (path.startsWith('/app/menu')) return { title: 'Cardápio', subtitle: 'Itens e preços com vigência' }
    if (path.startsWith('/app/import')) return { title: 'Importar Dados', subtitle: 'Planilhas Excel/CSV' }
    if (path.startsWith('/app/motoboys')) return { title: 'Painel de motoboys', subtitle: 'Saldo em mãos · CNH · Regras de pricing' }
    if (path.startsWith('/app/expedicao')) return { title: 'Expedição (caixa motoboy)', subtitle: 'Pegou / Devolveu · Saldo em tempo real' }
    if (path.startsWith('/app/contas')) return { title: 'Pagamentos motoboy', subtitle: 'Contas a pagar (payables)' }
    if (path.startsWith('/app/motoboy')) return { title: 'Fechamentos motoboy', subtitle: 'Motoboys · Cooperativa' }
    if (path.startsWith('/app/cooperatives')) return { title: 'Cooperativas / Parceiros', subtitle: 'Motoboys' }
    if (path.startsWith('/app/agenda')) return { title: 'Calendário da equipe', subtitle: 'Folgas · Faltas · Férias · Atestados · Trocas' }
    if (path.startsWith('/app/escala')) return { title: 'Modo escala (drag&drop)', subtitle: 'Mover folgas semanais' }
    if (path.startsWith('/app/absences/swaps')) return { title: 'Histórico de trocas', subtitle: 'Agenda & Presença' }
    if (path.startsWith('/app/absences')) return { title: 'Calendário da equipe', subtitle: 'Folgas · Faltas · Férias · Atestados · Trocas' }
    if (path.startsWith('/app/collaborators')) return { title: 'Gestão de Colaboradores', subtitle: 'RH · Folgas · Faltas · Férias' }
    if (path.startsWith('/app/mypanel')) return { title: 'Meu Painel', subtitle: 'Seus vales e consumos' }
    if (path.startsWith('/app/consumptions')) return { title: 'Consumo e vales', subtitle: 'Equipe' }
    if (path.startsWith('/app/equipe/checklists')) return { title: 'Checklists do dia', subtitle: 'Equipe' }
    if (path.startsWith('/app/equipe/dia')) return { title: 'Dia da equipe', subtitle: 'Equipe' }
    if (path.startsWith('/app/equipe/metas')) return { title: 'Metas e ranking', subtitle: 'Equipe' }
    if (path.startsWith('/app/equipe/extras')) return { title: 'Central de Extras', subtitle: 'Equipe' }
    if (path.startsWith('/app/dia')) return { title: 'Meu dia', subtitle: 'Colaborador' }
    if (path.startsWith('/app/payroll')) return { title: 'Folha de Pagamento', subtitle: 'Cálculo mensal CLT + Pro-labore' }
    if (path.startsWith('/app/bank-reconciliation')) return { title: 'Conciliação Bancária', subtitle: 'Importe extratos OFX e bata com fechamentos' }
    if (path.match(/\/collaborators\/[^/]+\/promote/)) return { title: 'Histórico de Cargo', subtitle: 'Promoção, mérito e reclassificação' }
    if (path.startsWith('/app/time-records')) return { title: 'Ponto Eletrônico', subtitle: 'App + Kiosk + Manual' }
    if (path.startsWith('/app/ats/blacklist')) return { title: 'Blacklist de candidatos', subtitle: 'Comunicação entre gerentes' }
    if (path.startsWith('/app/ats')) return { title: 'Recrutamento (ATS)', subtitle: 'Vagas · Candidatos · Processo seletivo' }
    if (path.startsWith('/app/provisions')) return { title: 'Provisões', subtitle: 'Férias · 1/3 · 13º — passivo trabalhista' }
    if (path.startsWith('/app/performance')) return { title: 'Avaliação de Desempenho', subtitle: 'PDI · ciclos trimestrais' }
    if (path.startsWith('/app/users')) return { title: 'Usuários & acessos', subtitle: 'Criar · editar · vincular a colaborador · papéis' }
    if (path.startsWith('/app/purchases')) return { title: 'Compras', subtitle: 'Compras com fornecedor / NF / anexos' }
    if (path.startsWith('/app/suppliers')) return { title: 'Fornecedores', subtitle: 'Cadastro global · compartilhado entre unidades' }
    if (path.startsWith('/app/bills')) return { title: 'Contas e Boletos', subtitle: 'Rateio entre unidades' }
    if (path.startsWith('/app/pos-venda/cancelamentos')) return { title: 'Pós-venda · Cancelamentos', subtitle: 'Fila para revisar e checagem diária' }
    if (path.startsWith('/app/vouchers/vender')) return { title: 'Vender voucher', subtitle: 'Balcão' }
    if (path.startsWith('/app/vouchers/nao-usados')) return { title: 'Vouchers não usados', subtitle: 'Abertos · vencendo · vencidos · reautorização' }
    if (path.startsWith('/app/vouchers/relatorio-vendas')) return { title: 'Vendas de voucher', subtitle: 'Vendidos · usados · vencidos' }
    if (path.startsWith('/app/vouchers')) return { title: 'Vouchers', subtitle: 'Templates, lotes e códigos de uso único' }
    if (path.startsWith('/app/wifi/settings')) return { title: 'Configuração WiFi', subtitle: 'SSID, controlador e portal de captura' }
    if (path.startsWith('/app/wifi/cashier')) return { title: 'Pós-venda WiFi', subtitle: 'Confirmar visitantes ativos' }
    if (path.startsWith('/app/wifi/denylist')) return { title: 'Denylist WiFi', subtitle: 'Funcionários, vizinhos e exclusões' }
    if (path.startsWith('/app/wifi/ranking')) return { title: 'Ranking de Atendentes', subtitle: 'Avaliações por colaborador' }
    if (path.startsWith('/app/wifi/feedback')) return { title: 'Caixa de Feedback', subtitle: 'Avaliações que precisam de atenção' }
    if (path.match(/\/app\/wifi\/visitors\/[^/]+/)) return { title: 'Detalhe do Visitante', subtitle: 'Histórico cross-unit + dispositivos' }
    if (path.startsWith('/app/wifi')) return { title: 'WiFi Analytics', subtitle: 'Visitantes · Recorrência · Cross-unit' }
    if (path.startsWith('/app/settings/sequencia-cortesia')) return { title: 'Sequência e cortesias', subtitle: 'Preço por dia · aniversariante/criança · itens da cortesia' }
    if (path.startsWith('/app/aniversariantes')) return { title: 'Aniversariantes do mês', subtitle: 'Lista para vender · vem do fechamento' }
    if (path.startsWith('/app/settings/cadastros')) return { title: 'Cadastros', subtitle: 'Pós-venda e vouchers' }
    if (path.startsWith('/app/settings/parameters')) return { title: 'Parâmetros da unidade', subtitle: 'Sistema' }
    if (path.startsWith('/app/settings/card-operators')) return { title: 'Operadoras', subtitle: 'Sistema' }
    if (path.startsWith('/app/settings/legal-entities')) return { title: 'CNPJs da unidade', subtitle: 'Cadastros operacionais' }
    if (path.startsWith('/app/settings/bank-accounts')) return { title: 'Contas bancárias', subtitle: 'Cadastros operacionais' }
    if (path.startsWith('/app/settings/users')) return { title: 'Senhas & ativação (rede)', subtitle: 'Reset de senha · ativar/desativar login' }
    if (path.startsWith('/app/settings/role-permissions')) return { title: 'Grupos e Permissões', subtitle: 'Padrões por papel' }
    if (path.startsWith('/app/settings/groups')) return { title: 'Grupos Personalizados', subtitle: 'Grupos dinâmicos com permissões customizadas' }
    if (path.startsWith('/app/reports')) return { title: 'Relatórios', subtitle: 'Calendário comercial 5-4-4' }
    if (path.startsWith('/app/cofre')) return { title: 'Cofre de credenciais', subtitle: 'Senhas e acessos cifrados (AES-256-GCM)' }
    if (path.startsWith('/app/manual')) return { title: 'Manual de uso', subtitle: 'Guia de configuração e operação' }
    if (isAllUnits) return { title: 'Sistema 4 Queijos', subtitle: `Todas as unidades · ${activeCount} ativas` }
    return { title: 'Sistema 4 Queijos', subtitle: selectedUnit?.name ?? `Módulo de operação · v${appVersion}` }
  }, [location.pathname, selectedUnit?.name, isAllUnits, activeCount, units, appVersion])

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  function handleSelectUnit(unitId: string | null) {
    selectUnit(unitId)
    setUnitDropdownOpen(false)
    setMobileMenuOpen(false)
  }

  if (!unitsLoaded) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background text-foreground-muted">
        <div className="animate-pulse">Carregando ambiente...</div>
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="flex min-h-dvh">
        <aside className="hidden w-72 shrink-0 flex-col bg-[#0d0c0b] text-zinc-200 md:flex"
          style={{ background: 'linear-gradient(to bottom, var(--sidebar-bg), var(--sidebar-bg))' }}>
          <div className="border-b border-white/10 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-[#c54812] to-[#e05a1a] text-white shadow-lg ring-2 ring-white/20">
                <img src="/logo.png" alt="4 Queijos" className="h-8 w-8 object-contain drop-shadow-md" />
              </div>
              <div className="leading-tight">
                <div className="text-lg font-bold text-white drop-shadow-sm">4 Queijos</div>
                <div className="text-sm text-zinc-300">Sistema de Operação · v{appVersion}</div>
              </div>
            </div>
          </div>

          <div className="px-3 pt-3">
            <label className="relative block">
              <span className="sr-only">Buscar item do menu</span>
              <input
                type="search"
                value={navSearch}
                onChange={(e) => setNavSearch(e.target.value)}
                placeholder="Buscar no menu..."
                className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/30"
              />
            </label>
          </div>
          <nav ref={navRef} className="flex-1 px-3 py-4 text-sm space-y-2">
            {/* TOPO FLUTUANTE — sem grupo */}
            <NavLink to="/app/mobile" className={navLinkClass}>
              <Smartphone className="h-4 w-4 text-zinc-400" />
              <span>Meu celular</span>
            </NavLink>

            {apkEnabled && (
              <NavLink to="/app/aplicativo" className={navLinkClass} title="Download do app, QR code, instruções e histórico de versões">
                <Download className="h-4 w-4 text-zinc-400" />
                <span>Aplicativo</span>
              </NavLink>
            )}

            {access.isAdmin && (
              <NavLink to="/app/inicio" className={navLinkClass}>
                <Home className="h-4 w-4 text-zinc-400" />
                <span>Início</span>
              </NavLink>
            )}

            {access.canViewDashboard && (
              <NavLink to="/app/dashboard" className={navLinkClass}>
                <LayoutDashboard className="h-4 w-4 text-zinc-400" />
                <span>Dashboard</span>
              </NavLink>
            )}

            {access.canUseClosing && (
              <NavLink to="/app/daily-panel" className={navLinkClass} title="Completude dos fechamentos de cada dia — ver o que falta lançar.">
                <BarChart3 className="h-4 w-4 text-zinc-400" />
                <span>Painel do dia</span>
              </NavLink>
            )}

            {access.isEmployeeOnly && (
              <NavLink to="/app/my-panel" className={navLinkClass}>
                <Users className="h-4 w-4 text-zinc-400" />
                <span>Meu Painel</span>
              </NavLink>
            )}

            {/* 📋 FECHAMENTO */}
            {access.canUseClosing && (
              <NavGroup label="Fechamento" icon="📋" defaultOpen={location.pathname.includes('/closing') || location.pathname.includes('/calendar') || location.pathname.startsWith('/app/weekly') || location.pathname.includes('/sector') || location.pathname.startsWith('/app/reports')} forceOpen={Boolean(navSearch)}>
                <NavLink
                  to={`/app/closing-pro/${businessDayLocalISO()}`}
                  className={navLinkClass}
                  title="Vendas, recebimentos, despesas, vales e motoboys em uma tela única. Entrada padrão do fechamento."
                >
                  <LayoutDashboard className="h-4 w-4 text-orange-400" />
                  <span>Hoje (gerente)</span>
                  <span className="ml-auto rounded-full bg-orange-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-orange-300">padrão</span>
                </NavLink>
                <NavLink
                  to={`/app/caixa/${businessDayLocalISO()}`}
                  className={navLinkClass}
                  title="Caixa do balcão: vendas, valor de cada maquininha e contagem da gaveta. Celular ou computador."
                >
                  <Wallet className="h-4 w-4 text-zinc-400" />
                  <span>Caixa do balcão</span>
                </NavLink>
                <NavLink
                  to={`/app/contagem/${businessDayLocalISO()}`}
                  className={navLinkClass}
                  title="Contagem de pizzas e pessoas da sequência, e o que a equipe já lançou."
                >
                  <Check className="h-4 w-4 text-zinc-400" />
                  <span>Contagem do gerente</span>
                </NavLink>
                {access.isManager && !access.isAdmin && (
                  <NavLink to="/app/settings/maquininhas" className={navLinkClass} title="Cadastro de maquininhas por CNPJ desta unidade.">
                    <Wallet className="h-4 w-4 text-zinc-400" />
                    <span>Maquininhas</span>
                  </NavLink>
                )}
                <NavLink
                  to="/app/calendar"
                  className={navLinkClass}
                  title="Calendário mensal com 1 card por dia. Abre o fechamento daquela data."
                >
                  <CalendarDays className="h-4 w-4 text-zinc-400" />
                  <span>Calendário de fechamentos</span>
                </NavLink>
                {access.canViewDashboard && (
                  <NavLink to="/app/weekly" className={navLinkClass}>
                    <BarChart3 className="h-4 w-4 text-zinc-400" />
                    <span>Visão da semana</span>
                  </NavLink>
                )}
                <NavLink to="/app/closing/monthly" className={navLinkClass}>
                  <CalendarDays className="h-4 w-4 text-zinc-400" />
                  <span>Fechamento mensal</span>
                </NavLink>
                {access.canUseReports && (
                  <NavLink to="/app/reports" className={navLinkClass} title="Calendário comercial 5-4-4">
                    <FileText className="h-4 w-4 text-zinc-400" />
                    <span>Calendário comercial</span>
                  </NavLink>
                )}
              </NavGroup>
            )}

            {/* 🛵 MOTOBOYS — grupo dedicado (atendente de expedição vê só Expedição + Fechamentos) */}
            {access.canUseExpedicao && (
              <NavGroup label="Motoboys" icon="🛵" defaultOpen={location.pathname.includes('/motoboy') || location.pathname.includes('/expedicao') || location.pathname.includes('/contas') || location.pathname.includes('/cooperatives')} forceOpen={Boolean(navSearch)}>
                {access.canUseTeamModules && (
                  <NavLink to="/app/motoboys" className={navLinkClass} title="Painel com cards: saldo em mãos, CNH, regra de pricing.">
                    <Truck className="h-4 w-4 text-zinc-400" />
                    <span>Painel de motoboys</span>
                  </NavLink>
                )}
                <NavLink to="/app/expedicao" className={navLinkClass} title="Controle de caixa: pegou / devolveu, saldo em tempo real.">
                  <Truck className="h-4 w-4 text-zinc-400" />
                  <span>Expedição (caixa)</span>
                </NavLink>
                <NavLink to="/app/motoboy" className={navLinkClass} title="Fechamentos dos motoboys com filtro por semana/mês.">
                  <Truck className="h-4 w-4 text-zinc-400" />
                  <span>Fechamentos</span>
                </NavLink>
                {access.canUseTeamModules && (
                  <NavLink to="/app/contas" className={navLinkClass} title="Contas a pagar dos motoboys (payables).">
                    <Wallet className="h-4 w-4 text-zinc-400" />
                    <span>Pagamentos</span>
                  </NavLink>
                )}
                {access.canUseTeamModules && (
                  <NavLink to="/app/cooperatives" className={navLinkClass}>
                    <Handshake className="h-4 w-4 text-zinc-400" />
                    <span>Cooperativas / Parceiros</span>
                  </NavLink>
                )}
              </NavGroup>
            )}

            {/* 👥 EQUIPE */}
            {access.canUseTeamModules && (
              <NavGroup label="Equipe" icon="👥" defaultOpen={location.pathname.includes('/collaborators') || location.pathname.includes('/consumptions') || location.pathname.includes('/extras') || location.pathname.includes('/ats') || location.pathname.includes('/performance') || location.pathname.includes('/job-titles')} forceOpen={Boolean(navSearch)}>
                <NavLink to="/app/collaborators" className={navLinkClass}>
                  <Users className="h-4 w-4 text-zinc-400" />
                  <span>Colaboradores</span>
                </NavLink>
                {access.isManager && (
                  <NavLink to="/app/pessoas" className={navLinkClass} title="Histórico por CPF: vínculos em todas as unidades, demissões, extras, ponto.">
                    <Search className="h-4 w-4 text-zinc-400" />
                    <span>Pessoa por CPF</span>
                  </NavLink>
                )}
                <NavLink to="/app/extras" className={navLinkClass}>
                  <UserPlus className="h-4 w-4 text-zinc-400" />
                  <span>Trabalhadores extras</span>
                </NavLink>
                <NavLink to="/app/equipe/extras" className={navLinkClass} title="Extras de todas as unidades: histórico, estrelas, status.">
                  <Star className="h-4 w-4 text-zinc-400" />
                  <span>Central de Extras</span>
                </NavLink>
                {access.isAdmin && (
                  <NavLink to="/app/job-titles" className={navLinkClass}>
                    <Briefcase className="h-4 w-4 text-zinc-400" />
                    <span>Cargos</span>
                  </NavLink>
                )}
                <NavLink to="/app/consumptions" className={navLinkClass}>
                  <Coins className="h-4 w-4 text-zinc-400" />
                  <span>Vales & Consumo</span>
                </NavLink>
                {diaEnabled && (
                  <NavGroup label="Dia da equipe" defaultOpen={location.pathname.includes('/equipe/')} forceOpen={Boolean(navSearch)}>
                    <NavLink to="/app/equipe/dia" className={navLinkClass} title="Quem abriu/fechou o dia, checklist e pendências.">
                      <ClipboardCheck className="h-4 w-4 text-zinc-400" />
                      <span>Hoje</span>
                    </NavLink>
                    <NavLink to="/app/equipe/checklists" className={navLinkClass} title="Montar o checklist por setor/turno.">
                      <ClipboardList className="h-4 w-4 text-zinc-400" />
                      <span>Checklists</span>
                    </NavLink>
                    <NavLink to="/app/equipe/metas" className={navLinkClass} title="Meta do mês, cadeados, produtos foco, destaque e ranking das lojas.">
                      <Trophy className="h-4 w-4 text-zinc-400" />
                      <span>Metas e ranking</span>
                    </NavLink>
                  </NavGroup>
                )}
                <NavLink to="/app/performance" className={navLinkClass}>
                  <TrendingUp className="h-4 w-4 text-zinc-400" />
                  <span>Avaliações (PDI)</span>
                </NavLink>
                <NavLink to="/app/collaborators/analytics" className={navLinkClass} title="Classificação A/B/C por custo.">
                  <TrendingUp className="h-4 w-4 text-zinc-400" />
                  <span>Curva ABC</span>
                </NavLink>

                {/* Sub-grupo ATS recolhível */}
                <NavGroup label="Recrutamento (ATS)" defaultOpen={location.pathname.includes('/ats')} forceOpen={Boolean(navSearch)}>
                  <NavLink to="/app/ats" className={navLinkClass}>
                    <Users className="h-4 w-4 text-zinc-400" />
                    <span>Kanban de candidatos</span>
                  </NavLink>
                  <NavLink to="/app/ats/dashboard" className={navLinkClass}>
                    <BarChart3 className="h-4 w-4 text-zinc-400" />
                    <span>Dashboard</span>
                  </NavLink>
                  <NavLink to="/app/ats/repescagem" className={navLinkClass}>
                    <Sliders className="h-4 w-4 text-zinc-400" />
                    <span>Repescagem</span>
                  </NavLink>
                  <NavLink to="/app/ats/perguntas" className={navLinkClass}>
                    <BookOpen className="h-4 w-4 text-zinc-400" />
                    <span>Perguntas</span>
                  </NavLink>
                  <NavLink to="/app/ats/slots" className={navLinkClass}>
                    <CalendarDays className="h-4 w-4 text-zinc-400" />
                    <span>Janelas de entrevista</span>
                  </NavLink>
                  <NavLink to="/app/ats/admissao" className={navLinkClass}>
                    <Check className="h-4 w-4 text-zinc-400" />
                    <span>Admissão</span>
                  </NavLink>
                  <NavLink to="/app/ats/blacklist" className={navLinkClass}>
                    <Shield className="h-4 w-4 text-zinc-400" />
                    <span>Blacklist</span>
                  </NavLink>
                </NavGroup>
              </NavGroup>
            )}

            {/* 📅 AGENDA & PRESENÇA — calendário unificado */}
            {access.canUseTeamModules && (
              <NavGroup label="Agenda & Presença" icon="📅" defaultOpen={location.pathname.includes('/agenda') || location.pathname.includes('/escala') || location.pathname.includes('/absences') || location.pathname.includes('/time-records')} forceOpen={Boolean(navSearch)}>
                <NavLink to="/app/agenda" className={navLinkClass} title="Folgas, faltas, férias, atestados e trocas — tudo num único calendário com filtros por camada.">
                  <CalendarDays className="h-4 w-4 text-orange-400" />
                  <span>Calendário da equipe</span>
                  <span className="ml-auto rounded-full bg-orange-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-orange-300">novo</span>
                </NavLink>
                <NavLink to="/app/calendario-trabalho" className={navLinkClass} title="Quem trabalha em cada dia, por setor. Arraste para mudar folga ou trocar; imprima a semana.">
                  <CalendarDays className="h-4 w-4 text-orange-400" />
                  <span>Calendário de trabalho</span>
                </NavLink>
                <NavLink to="/app/escala" className={navLinkClass} title="Drag-and-drop semanal para mover folgas (modo operacional).">
                  <CalendarClock className="h-4 w-4 text-zinc-400" />
                  <span>Modo escala (drag&drop)</span>
                </NavLink>
                <NavLink to="/app/absences/swaps" className={navLinkClass}>
                  <TrendingUp className="h-4 w-4 text-zinc-400" />
                  <span>Histórico de trocas</span>
                </NavLink>
                <NavLink to="/app/time-records" className={navLinkClass}>
                  <CalendarClock className="h-4 w-4 text-zinc-400" />
                  <span>Ponto eletrônico</span>
                </NavLink>
              </NavGroup>
            )}

            {/* 💰 FINANCEIRO */}
            {(access.canUseFinance || access.canUseTeamModules) && (
              <NavGroup label="Financeiro" icon="💰" defaultOpen={location.pathname.includes('/bills') || location.pathname.includes('/purchases') || location.pathname.includes('/suppliers') || location.pathname.includes('/expenses') || location.pathname.includes('/payroll') || location.pathname.includes('/provisions') || location.pathname.includes('/bank-reconciliation') || location.pathname.includes('/fiados') || location.pathname.includes('/expense-categories') || location.pathname.includes('/payment-methods') || location.pathname.includes('/settings/legal-entities') || location.pathname.includes('/settings/bank-accounts') || location.pathname.includes('/settings/card-operators')} forceOpen={Boolean(navSearch)}>
                {access.canUseFinance && (
                  <NavLink to="/app/bills" className={navLinkClass}>
                    <Receipt className="h-4 w-4 text-zinc-400" />
                    <span>Contas a pagar</span>
                  </NavLink>
                )}
                {access.canUseTeamModules && (
                  <>
                    <NavLink to="/app/purchases" className={navLinkClass}>
                      <Receipt className="h-4 w-4 text-zinc-400" />
                      <span>Compras (com NF)</span>
                    </NavLink>
                    <NavLink to="/app/expenses" className={navLinkClass}>
                      <Coins className="h-4 w-4 text-zinc-400" />
                      <span>Despesas avulsas</span>
                    </NavLink>
                    {access.isAdmin && (
                      <NavLink to="/app/fiados" className={navLinkClass}>
                        <AlertCircle className="h-4 w-4 text-orange-400" />
                        <span>Fiados pendentes</span>
                      </NavLink>
                    )}
                    <NavLink to="/app/payroll" className={navLinkClass}>
                      <FileText className="h-4 w-4 text-zinc-400" />
                      <span>Folha de pagamento</span>
                    </NavLink>
                    <NavLink to="/app/provisions" className={navLinkClass}>
                      <Landmark className="h-4 w-4 text-zinc-400" />
                      <span>Provisões (férias / 13º)</span>
                    </NavLink>
                    <NavLink to="/app/bank-reconciliation" className={navLinkClass}>
                      <Landmark className="h-4 w-4 text-zinc-400" />
                      <span>Conciliação bancária</span>
                    </NavLink>
                    <NavLink to="/app/suppliers" className={navLinkClass}>
                      <Building className="h-4 w-4 text-zinc-400" />
                      <span>Fornecedores</span>
                    </NavLink>

                    {/* Sub-grupo cadastros financeiros — só admin */}
                    {access.isAdmin && (
                      <NavGroup label="Cadastros financeiros" defaultOpen={location.pathname.includes('/expense-categories') || location.pathname.includes('/payment-methods') || location.pathname.includes('/settings/legal-entities') || location.pathname.includes('/settings/bank-accounts') || location.pathname.includes('/settings/card-operators')} forceOpen={Boolean(navSearch)}>
                        <NavLink to="/app/expense-categories" className={navLinkClass}>
                          <Coins className="h-4 w-4 text-zinc-400" />
                          <span>Categorias de despesa</span>
                        </NavLink>
                        <NavLink to="/app/payment-methods" className={navLinkClass}>
                          <Wallet className="h-4 w-4 text-zinc-400" />
                          <span>Formas de pagamento</span>
                        </NavLink>
                        <NavLink to="/app/settings/legal-entities" className={navLinkClass}>
                          <Building className="h-4 w-4 text-zinc-400" />
                          <span>CNPJs da unidade</span>
                        </NavLink>
                        <NavLink to="/app/settings/bank-accounts" className={navLinkClass}>
                          <Landmark className="h-4 w-4 text-zinc-400" />
                          <span>Contas bancárias</span>
                        </NavLink>
                        <NavLink to="/app/settings/maquininhas" className={navLinkClass}>
                          <Wallet className="h-4 w-4 text-zinc-400" />
                          <span>CNPJs e maquininhas</span>
                        </NavLink>
                        <NavLink to="/app/settings/card-operators" className={navLinkClass}>
                          <Settings className="h-4 w-4 text-zinc-400" />
                          <span>Operadoras de cartão</span>
                        </NavLink>
                      </NavGroup>
                    )}
                  </>
                )}
              </NavGroup>
            )}

            {/* 🍕 CARDÁPIO & VOUCHERS — unificado */}
            {access.canUseTeamModules && (
              <NavGroup label="Cardápio & Vouchers" icon="🍕" defaultOpen={location.pathname.includes('/menu') || location.pathname.includes('/cardapio') || location.pathname.includes('/voucher') || location.pathname.includes('/product-catalog') || location.pathname.includes('/cortesias')} forceOpen={Boolean(navSearch)}>
                <NavLink to="/app/menu" className={navLinkClass}>
                  <UtensilsCrossed className="h-4 w-4 text-zinc-400" />
                  <span>Cardápio</span>
                </NavLink>
                <NavLink to="/app/product-catalog" className={navLinkClass}>
                  <Package className="h-4 w-4 text-zinc-400" />
                  <span>Catálogo de produtos</span>
                </NavLink>
                {access.isAdmin && (
                  <NavLink to="/app/cardapio/import" className={navLinkClass}>
                    <Upload className="h-4 w-4 text-zinc-400" />
                    <span>Importar do PDV (PDF)</span>
                  </NavLink>
                )}

                {/* Sub-grupo Vouchers */}
                {vouchersEnabled && (access.canUseFinance || access.canUseTeamModules) && (
                  <NavGroup label="Vouchers" defaultOpen={location.pathname.includes('/voucher') || location.pathname.includes('/cortesias')} forceOpen={Boolean(navSearch)}>
                    <NavLink to="/app/vouchers/vender" className={navLinkClass}>
                      <Ticket className="h-4 w-4 text-zinc-400" />
                      <span>Vender voucher (balcão)</span>
                    </NavLink>
                    <NavLink to="/app/voucher-terminal" className={navLinkClass}>
                      <Ticket className="h-4 w-4 text-zinc-400" />
                      <span>Terminal (ativar/redimir)</span>
                    </NavLink>
                    {(access.isAdmin || access.isManager) && (
                      <NavLink to="/app/vouchers/relatorio-vendas" className={navLinkClass}>
                        <Ticket className="h-4 w-4 text-zinc-400" />
                        <span>Vendas de voucher</span>
                      </NavLink>
                    )}
                    {(access.isAdmin || access.isManager) && (
                      <NavLink to="/app/aniversariantes" className={navLinkClass}>
                        <Ticket className="h-4 w-4 text-zinc-400" />
                        <span>Aniversariantes do mês</span>
                      </NavLink>
                    )}
                    {(access.isAdmin || access.isManager) && (
                      <NavLink to="/app/vouchers/nao-usados" className={navLinkClass}>
                        <Ticket className="h-4 w-4 text-zinc-400" />
                        <span>Vouchers não usados</span>
                      </NavLink>
                    )}
                    {access.canUseTeamModules && (
                      <NavLink to="/app/vouchers" className={navLinkClass}>
                        <Ticket className="h-4 w-4 text-zinc-400" />
                        <span>Templates e lotes</span>
                      </NavLink>
                    )}
                    {access.isAdmin && (
                      <NavLink to="/app/empresas" className={navLinkClass} title="Parceira, compradora de vouchers, fornecedora, cliente — telas do Portal de empresas">
                        <Handshake className="h-4 w-4 text-zinc-400" />
                        <span>Empresas e papéis</span>
                      </NavLink>
                    )}
                    {access.isAdmin && (
                      <NavLink to="/app/cortesias-giftcards" className={navLinkClass}>
                        <Gift className="h-4 w-4 text-zinc-400" />
                        <span>Cortesias & passivo</span>
                      </NavLink>
                    )}
                  </NavGroup>
                )}
              </NavGroup>
            )}

            {/* 📞 PÓS-VENDA — cancelamentos para revisar (perto de Vouchers) */}
            {canReviewCancellations && (
              <NavGroup label="Pós-venda" icon="📞" defaultOpen={location.pathname.includes('/pos-venda')} forceOpen={Boolean(navSearch)}>
                <NavLink to="/app/pos-venda/cancelamentos" className={navLinkClass} title="Cancelamentos para revisar e checagem diária">
                  <ClipboardCheck className="h-4 w-4 text-zinc-400" />
                  <span>Cancelamentos para revisar</span>
                </NavLink>
              </NavGroup>
            )}

            {/* 💬 CLIENTES — CRM + WiFi */}
            {(access.canUseTeamModules || (wifiEnabled && access.canViewDashboard)) && (
              <NavGroup label="Clientes" icon="💬" defaultOpen={location.pathname.includes('/crm') || location.pathname.includes('/wifi')} forceOpen={Boolean(navSearch)}>
                <NavLink to="/app/crm/inbox" className={navLinkClass} title="Conversas multicanal (WhatsApp, Instagram, etc).">
                  <MessageSquare className="h-4 w-4 text-zinc-400" />
                  <span>CRM Inbox</span>
                </NavLink>

                {/* Sub-grupo WiFi Analytics */}
                {wifiEnabled && (access.canViewDashboard || access.canUseTeamModules) && (
                  <NavGroup label="WiFi Analytics" defaultOpen={location.pathname.includes('/wifi')} forceOpen={Boolean(navSearch)}>
                    <NavLink to="/app/wifi/visitors" className={navLinkClass}>
                      <Wifi className="h-4 w-4 text-zinc-400" />
                      <span>Visitantes</span>
                    </NavLink>
                    <NavLink to="/app/wifi/cashier" className={navLinkClass}>
                      <Receipt className="h-4 w-4 text-zinc-400" />
                      <span>Pós-venda</span>
                    </NavLink>
                    <NavLink to="/app/wifi/feedback" className={navLinkClass}>
                      <MessageSquare className="h-4 w-4 text-zinc-400" />
                      <span>Feedback</span>
                    </NavLink>
                    <NavLink to="/app/wifi/ranking" className={navLinkClass}>
                      <Trophy className="h-4 w-4 text-zinc-400" />
                      <span>Ranking de atendentes</span>
                    </NavLink>
                    <NavLink to="/app/wifi/denylist" className={navLinkClass}>
                      <ShieldOff className="h-4 w-4 text-zinc-400" />
                      <span>Denylist</span>
                    </NavLink>
                    {canUseSystemAdmin && (
                      <NavLink to="/app/wifi/settings" className={navLinkClass}>
                        <Settings className="h-4 w-4 text-zinc-400" />
                        <span>Configurar WiFi</span>
                      </NavLink>
                    )}
                  </NavGroup>
                )}
              </NavGroup>
            )}

            {/* ⚙️ SISTEMA (admin only) */}
            {canUseSystemAdmin && (
              <NavGroup label="Sistema" icon="⚙️" defaultOpen={location.pathname.includes('/settings/units') || location.pathname.includes('/settings/parameters') || location.pathname.includes('/settings/users') || location.pathname.includes('/settings/role-permissions') || location.pathname.includes('/settings/groups') || location.pathname.includes('/auditoria') || location.pathname.includes('/cofre') || location.pathname.includes('/import') || location.pathname.includes('/recursos')} forceOpen={Boolean(navSearch)}>
                <NavLink to="/app/settings/units" className={navLinkClass}>
                  <Store className="h-4 w-4 text-zinc-400" />
                  <span>Unidades</span>
                </NavLink>
                <NavLink to="/app/settings/parameters" className={navLinkClass}>
                  <Sliders className="h-4 w-4 text-zinc-400" />
                  <span>Parâmetros</span>
                </NavLink>
                {access.isAdmin && (
                  <NavLink to="/app/settings/cadastros" className={navLinkClass} title="Canais do pedido, status/resultados do pós-venda e produtos de voucher">
                    <Sliders className="h-4 w-4 text-zinc-400" />
                    <span>Cadastros</span>
                  </NavLink>
                )}
                {access.isAdmin && (
                  <NavLink to="/app/settings/sequencia-cortesia" className={navLinkClass} title="Preço da sequência por dia, aniversariante/criança e itens da cortesia">
                    <Sliders className="h-4 w-4 text-zinc-400" />
                    <span>Sequência e cortesias</span>
                  </NavLink>
                )}
                <NavLink to="/app/users" className={navLinkClass} title="Criar usuário, editar, vincular a colaborador, dar papel (gerente/colaborador) na unidade">
                  <UserCog className="h-4 w-4 text-zinc-400" />
                  <span>Usuários &amp; acessos</span>
                </NavLink>
                <NavLink to="/app/settings/users" className={navLinkClass} title="Visão de rede: reset de senha e ativar/desativar login">
                  <KeyRound className="h-4 w-4 text-zinc-400" />
                  <span>Senhas &amp; ativação (rede)</span>
                </NavLink>
                <NavLink to="/app/settings/role-permissions" className={navLinkClass}>
                  <Shield className="h-4 w-4 text-zinc-400" />
                  <span>Grupos e Permissões</span>
                </NavLink>
                <NavLink to="/app/settings/groups" className={navLinkClass}>
                  <Users2 className="h-4 w-4 text-zinc-400" />
                  <span>Grupos personalizados</span>
                </NavLink>
                <NavLink to="/app/auditoria" className={navLinkClass}>
                  <FileText className="h-4 w-4 text-zinc-400" />
                  <span>Auditoria</span>
                </NavLink>
                <NavLink to="/app/cofre" className={navLinkClass}>
                  <Lock className="h-4 w-4 text-zinc-400" />
                  <span>Cofre de senhas</span>
                </NavLink>
                <NavLink to="/app/import" className={navLinkClass}>
                  <Upload className="h-4 w-4 text-zinc-400" />
                  <span>Importar dados</span>
                </NavLink>
                {hasAdminInAnyUnit && (
                  <NavLink to="/app/recursos" className={navLinkClass}>
                    <BarChart3 className="h-4 w-4 text-zinc-400" />
                    <span>Recursos do servidor</span>
                  </NavLink>
                )}
              </NavGroup>
            )}

            {/* ACESSO — Módulos (owner/admin_rede) + Usuários (fallback p/ user-manager sem ser admin) */}
            {((access.canManageUsers && !canUseSystemAdmin) || hasAdminInAnyUnit) && (
              <NavGroup label="Acesso" icon="🔐" defaultOpen={location.pathname.includes('/users') || location.pathname.includes('/acesso')} forceOpen={Boolean(navSearch)}>
                {hasAdminInAnyUnit && (
                  <NavLink to="/app/acesso/modulos" className={navLinkClass} title="Ligar/desligar módulos da rede (ex.: Dia do colaborador, APK Android)">
                    <Boxes className="h-4 w-4 text-zinc-400" />
                    <span>Módulos</span>
                  </NavLink>
                )}
                {access.canManageUsers && !canUseSystemAdmin && (
                  <NavLink to="/app/users" className={navLinkClass}>
                    <Shield className="h-4 w-4 text-zinc-400" />
                    <span>Usuários</span>
                  </NavLink>
                )}
              </NavGroup>
            )}

            {/* AJUDA */}
            {manualEnabled && (
              <NavGroup label="Ajuda" icon="📖" defaultOpen={location.pathname.includes('/manual')} forceOpen={Boolean(navSearch)}>
                <NavLink to="/app/manual" className={navLinkClass}>
                  <BookOpen className="h-4 w-4 text-zinc-400" />
                  <span>Manual de uso</span>
                </NavLink>
              </NavGroup>
            )}
          </nav>

          <div className="border-t border-white/10 px-5 py-4">
            <div className="text-sm font-semibold text-white">{user?.displayName || user?.email || '—'}</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-400">
              {isAllUnits ? (
                <Layers className="h-3 w-3 flex-shrink-0" />
              ) : selectedUnit ? (
                <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: selectedUnit.color ?? 'var(--accent)' }} />
              ) : null}
              {isAllUnits ? 'Todas as unidades' : (selectedUnit?.name || 'Selecionar unidade')}
            </div>

            <div className="mt-4 flex flex-col gap-2 border-t border-white/10 pt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Tema</div>
              <div className="grid grid-cols-2 gap-2">
                {useThemeStore.getState().availableThemes.map((t) => {
                  const themeColors = themes[t]
                  return (
                    <button
                      key={t}
                      onClick={() => useThemeStore.getState().setTheme(t)}
                      className={`flex flex-col items-center gap-1.5 rounded-lg px-2 py-2 text-xs font-medium transition-colors ${
                        theme === t
                          ? 'bg-white/15 text-white'
                          : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                      }`}
                    >
                      {/* Color swatches preview */}
                      <div className="flex gap-0.5 rounded">
                        <div
                          className="h-2.5 w-2.5 rounded-l-sm"
                          style={{ backgroundColor: themeColors.bgPrimary }}
                          title="Background"
                        />
                        <div
                          className="h-2.5 w-2.5"
                          style={{ backgroundColor: themeColors.accent }}
                          title="Accent"
                        />
                        <div
                          className="h-2.5 w-2.5 rounded-r-sm"
                          style={{ backgroundColor: themeColors.sidebarBg }}
                          title="Sidebar"
                        />
                      </div>
                      {t === 'light' && <Sun className="h-4 w-4" />}
                      {t === 'dark' && <Moon className="h-4 w-4" />}
                      {t === 'clean' && <Palette className="h-4 w-4" />}
                      {t === 'moderno' && <Sparkles className="h-4 w-4" />}
                      <span>{THEME_LABELS[t]}</span>
                      {theme === t && <Check className="h-3 w-3" />}
                    </button>
                  )
                })}
              </div>
            </div>

            <button
              type="button"
              className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
              onClick={() => void handleLogout()}
            >
              <LogOut className="h-4 w-4" />
              Sair
            </button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-10 border-b border-border-color bg-surface/80 backdrop-blur pt-safe">
            <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-4">
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-border-color bg-surface md:hidden"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </button>

              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-foreground">{header.title}</div>
                <div className="truncate text-xs text-foreground-muted">{header.subtitle}</div>
              </div>

              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-border-color bg-surface hover:bg-surface-alt transition-colors"
                  aria-label={`Mudar para tema`}
                  title={`Tema: ${THEME_LABELS[theme]} (toque para trocar)`}
                >
                  {theme === 'light' ? (
                    <Moon className="h-4 w-4 text-foreground" />
                  ) : (
                    <Sun className="h-4 w-4 text-foreground" />
                  )}
                </button>
                <div ref={unitDropdownRef} className="relative hidden sm:block">
                  <button
                    type="button"
                    onClick={() => setUnitDropdownOpen((open) => !open)}
                    className="flex h-9 items-center gap-2 rounded-md border border-border-color bg-surface px-3 text-sm hover:bg-surface-alt text-foreground"
                    aria-label="Selecionar unidade"
                  >
                    {selectedUnitId === null ? (
                      <>
                        <Layers className="h-3.5 w-3.5" />
                        <span className="max-w-32 truncate">Todas as unidades</span>
                      </>
                    ) : (
                      <>
                        {selectedUnit ? <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: selectedUnit.color ?? 'var(--accent)' }} /> : null}
                        <span className="max-w-32 truncate">{selectedUnit?.name ?? 'Selecionar...'}</span>
                      </>
                    )}
                    <svg className="h-3.5 w-3.5 text-zinc-400" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {unitDropdownOpen ? (
                    <div className="absolute right-0 top-10 z-50 min-w-40 rounded-md border border-border-color bg-surface py-1 shadow-lg">
                      {canSwitchToAllUnits ? (
                        <button
                          type="button"
                          onClick={() => {
                            selectUnit(null)
                            setUnitDropdownOpen(false)
                            setMobileMenuOpen(false)
                          }}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-alt text-foreground ${selectedUnitId === null ? 'font-semibold' : ''}`}
                        >
                          <Layers className="h-3.5 w-3.5 text-zinc-400" />
                          Todas as unidades
                        </button>
                      ) : null}
                      {canSwitchToAllUnits ? <div className="my-1 border-t border-border-color" /> : null}
                      {units.filter((unit) => unit.active ?? true).map((unit) => (
                        <button
                          key={unit.id}
                          type="button"
                          onClick={() => handleSelectUnit(unit.id)}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-alt text-foreground ${unit.id === selectedUnitId ? 'font-semibold' : ''}`}
                        >
                          <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: unit.color ?? 'var(--accent)' }} />
                          {unit.name}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <select
                  value={selectedUnitId === null ? '__all__' : (selectedUnitId ?? '')}
                  onChange={(event) => {
                    const value = event.target.value
                    if (value === '__all__') selectUnit(null)
                    else if (value) selectUnit(value)
                  }}
                  className="h-9 rounded-md border border-border-color bg-surface px-3 text-sm sm:hidden text-foreground"
                  aria-label="Selecionar unidade"
                >
                  {canSwitchToAllUnits ? <option value="__all__">Todas as unidades</option> : null}
                  {units.length === 0 ? <option value="">Carregando...</option> : null}
                  {units.filter((unit) => unit.active ?? true).map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className="inline-flex h-9 items-center gap-2 rounded-md border border-border-color bg-surface px-3 text-sm hover:bg-surface-alt text-foreground"
                  onClick={() => void handleLogout()}
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Sair</span>
                </button>
              </div>
            </div>
          </header>

          {mobileMenuOpen ? (
            <div className="fixed inset-0 z-50 bg-black/50 md:hidden" onClick={() => setMobileMenuOpen(false)} />
          ) : null}

          {mobileMenuOpen ? (
            <div className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-[#0d0c0b] text-zinc-200 md:hidden animate-in slide-in-from-left"
              style={{ background: 'linear-gradient(to bottom, var(--sidebar-bg), var(--sidebar-bg))' }}>
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-gradient-to-br from-[#c54812] to-[#e05a1a] text-white shadow-sm ring-1 ring-white/20">
                  <img src="/logo.png" alt="4 Queijos" className="h-6 w-6 object-contain drop-shadow-sm" />
                </div>
                <div className="text-sm font-semibold text-white drop-shadow-sm">4 Queijos</div>
              </div>
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="border-b border-white/10 px-5 py-3">
                {/* Celular: unidade num dropdown compacto — a lista de botões (uma linha por
                    unidade) empurrava o menu pra baixo e sobrava pouco espaço pra navegação. */}
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-zinc-500" htmlFor="mobile-unit-select">Unidade</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                    {selectedUnitId === null ? (
                      <Layers className="h-3.5 w-3.5 text-zinc-400" />
                    ) : (
                      <span className="block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: selectedUnit?.color ?? 'var(--accent)' }} />
                    )}
                  </span>
                  <select
                    id="mobile-unit-select"
                    value={selectedUnitId === null ? '__all__' : (selectedUnitId ?? '')}
                    onChange={(event) => {
                      const value = event.target.value
                      handleSelectUnit(value === '__all__' ? null : value || null)
                    }}
                    className="h-11 w-full appearance-none rounded-lg border border-white/15 bg-white/10 pl-9 pr-9 text-sm font-medium text-white outline-none focus:border-white/40"
                    aria-label="Selecionar unidade"
                  >
                    {canSwitchToAllUnits ? <option value="__all__" className="text-zinc-900">Todas as unidades</option> : null}
                    {units.length === 0 ? <option value="" className="text-zinc-900">Carregando...</option> : null}
                    {units.filter((unit) => unit.active ?? true).map((unit) => (
                      <option key={unit.id} value={unit.id} className="text-zinc-900">
                        {unit.name}
                      </option>
                    ))}
                  </select>
                  <svg className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>

              <div className="px-3 pt-3">
                <label className="relative block">
                  <span className="sr-only">Buscar item do menu</span>
                  <input
                    type="search"
                    value={navSearch}
                    onChange={(e) => setNavSearch(e.target.value)}
                    placeholder="Buscar no menu..."
                    className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/30"
                  />
                </label>
              </div>
              <nav ref={mobileNavRef} className="flex-1 overflow-y-auto px-3 py-4 text-sm space-y-1">
                {/* TOPO FLUTUANTE */}
                {apkEnabled && (
                  <NavLink to="/app/aplicativo" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                    <Download className="h-4 w-4 text-zinc-400" />
                    <span>Aplicativo (download)</span>
                  </NavLink>
                )}
                {access.isAdmin && (
                  <NavLink to="/app/inicio" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                    <Home className="h-4 w-4 text-zinc-400" />
                    <span>Início</span>
                  </NavLink>
                )}
                {access.canViewDashboard && (
                  <NavLink to="/app/dashboard" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                    <LayoutDashboard className="h-4 w-4 text-zinc-400" />
                    <span>Dashboard</span>
                  </NavLink>
                )}
                {access.canUseClosing && (
                  <NavLink to="/app/daily-panel" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                    <BarChart3 className="h-4 w-4 text-zinc-400" />
                    <span>Painel do dia</span>
                  </NavLink>
                )}
                {access.isEmployeeOnly && (
                  <NavLink to="/app/my-panel" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                    <Users className="h-4 w-4 text-zinc-400" />
                    <span>Meu Painel</span>
                  </NavLink>
                )}

                {/* 📋 FECHAMENTO */}
                {access.canUseClosing && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">📋 Fechamento</div>
                    <NavLink to={`/app/closing-pro/${businessDayLocalISO()}`} onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <LayoutDashboard className="h-4 w-4 text-orange-400" />
                      <span>Hoje (gerente)</span>
                      <span className="ml-auto rounded-full bg-orange-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-orange-300">padrão</span>
                    </NavLink>
                    <NavLink to="/app/calendar" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <CalendarDays className="h-4 w-4 text-zinc-400" />
                      <span>Calendário de fechamentos</span>
                    </NavLink>
                    {access.canViewDashboard && (
                      <NavLink to="/app/weekly" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <BarChart3 className="h-4 w-4 text-zinc-400" />
                        <span>Visão da semana</span>
                      </NavLink>
                    )}
                    <NavLink to="/app/closing/monthly" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <CalendarDays className="h-4 w-4 text-zinc-400" />
                      <span>Fechamento mensal</span>
                    </NavLink>
                    {access.canUseReports && (
                      <NavLink to="/app/reports" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <FileText className="h-4 w-4 text-zinc-400" />
                        <span>Calendário comercial</span>
                      </NavLink>
                    )}
                  </>
                )}

                {/* 🛵 MOTOBOYS */}
                {access.canUseExpedicao && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">🛵 Motoboys</div>
                    {access.canUseTeamModules && (
                      <NavLink to="/app/motoboys" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Truck className="h-4 w-4 text-zinc-400" />
                        <span>Painel de motoboys</span>
                      </NavLink>
                    )}
                    <NavLink to="/app/expedicao" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <Truck className="h-4 w-4 text-zinc-400" />
                      <span>Expedição (caixa)</span>
                    </NavLink>
                    <NavLink to="/app/motoboy" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <Truck className="h-4 w-4 text-zinc-400" />
                      <span>Fechamentos</span>
                    </NavLink>
                    {access.canUseTeamModules && (
                      <NavLink to="/app/contas" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Wallet className="h-4 w-4 text-zinc-400" />
                        <span>Pagamentos</span>
                      </NavLink>
                    )}
                    {access.canUseTeamModules && (
                      <NavLink to="/app/cooperatives" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Handshake className="h-4 w-4 text-zinc-400" />
                        <span>Cooperativas / Parceiros</span>
                      </NavLink>
                    )}
                  </>
                )}

                {/* 👥 EQUIPE */}
                {access.canUseTeamModules && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">👥 Equipe</div>
                    <NavLink to="/app/collaborators" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <Users className="h-4 w-4 text-zinc-400" />
                      <span>Colaboradores</span>
                    </NavLink>
                    {access.isManager && (
                      <NavLink to="/app/pessoas" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Search className="h-4 w-4 text-zinc-400" />
                        <span>Pessoa por CPF</span>
                      </NavLink>
                    )}
                    <NavLink to="/app/extras" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <UserPlus className="h-4 w-4 text-zinc-400" />
                      <span>Trabalhadores extras</span>
                    </NavLink>
                    <NavLink to="/app/equipe/extras" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <Star className="h-4 w-4 text-zinc-400" />
                      <span>Central de Extras</span>
                    </NavLink>
                    {access.isAdmin && (
                      <NavLink to="/app/job-titles" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Briefcase className="h-4 w-4 text-zinc-400" />
                        <span>Cargos</span>
                      </NavLink>
                    )}
                    <NavLink to="/app/consumptions" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <Coins className="h-4 w-4 text-zinc-400" />
                      <span>Vales & Consumo</span>
                    </NavLink>
                    {diaEnabled && (
                      <>
                        <NavLink to="/app/equipe/dia" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <ClipboardCheck className="h-4 w-4 text-zinc-400" />
                          <span>Dia da equipe</span>
                        </NavLink>
                        <NavLink to="/app/equipe/checklists" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <ClipboardList className="h-4 w-4 text-zinc-400" />
                          <span>Checklists do dia</span>
                        </NavLink>
                        <NavLink to="/app/equipe/metas" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Trophy className="h-4 w-4 text-zinc-400" />
                          <span>Metas e ranking</span>
                        </NavLink>
                      </>
                    )}
                    <NavLink to="/app/performance" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <TrendingUp className="h-4 w-4 text-zinc-400" />
                      <span>Avaliações (PDI)</span>
                    </NavLink>
                    <NavLink to="/app/collaborators/analytics" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <TrendingUp className="h-4 w-4 text-zinc-400" />
                      <span>Curva ABC</span>
                    </NavLink>
                    <NavLink to="/app/ats" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <Users className="h-4 w-4 text-zinc-400" />
                      <span>Recrutamento (ATS)</span>
                    </NavLink>
                  </>
                )}

                {/* 📅 AGENDA & PRESENÇA */}
                {access.canUseTeamModules && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">📅 Agenda & Presença</div>
                    <NavLink to="/app/agenda" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <CalendarDays className="h-4 w-4 text-orange-400" />
                      <span>Calendário da equipe</span>
                    </NavLink>
                    <NavLink to="/app/calendario-trabalho" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <CalendarDays className="h-4 w-4 text-orange-400" />
                      <span>Calendário de trabalho</span>
                    </NavLink>
                    <NavLink to="/app/escala" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <CalendarClock className="h-4 w-4 text-zinc-400" />
                      <span>Modo escala (drag&drop)</span>
                    </NavLink>
                    <NavLink to="/app/absences/swaps" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <TrendingUp className="h-4 w-4 text-zinc-400" />
                      <span>Histórico de trocas</span>
                    </NavLink>
                    <NavLink to="/app/time-records" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <CalendarClock className="h-4 w-4 text-zinc-400" />
                      <span>Ponto eletrônico</span>
                    </NavLink>
                  </>
                )}

                {/* 💰 FINANCEIRO */}
                {(access.canUseFinance || access.canUseTeamModules) && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">💰 Financeiro</div>
                    {access.canUseFinance && (
                      <NavLink to="/app/bills" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Receipt className="h-4 w-4 text-zinc-400" />
                        <span>Contas a pagar</span>
                      </NavLink>
                    )}
                    {access.canUseTeamModules && (
                      <>
                        <NavLink to="/app/purchases" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Receipt className="h-4 w-4 text-zinc-400" />
                          <span>Compras (com NF)</span>
                        </NavLink>
                        <NavLink to="/app/expenses" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Coins className="h-4 w-4 text-zinc-400" />
                          <span>Despesas avulsas</span>
                        </NavLink>
                        {access.isAdmin && (
                          <NavLink to="/app/fiados" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                            <AlertCircle className="h-4 w-4 text-orange-400" />
                            <span>Fiados pendentes</span>
                          </NavLink>
                        )}
                        <NavLink to="/app/payroll" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <FileText className="h-4 w-4 text-zinc-400" />
                          <span>Folha de pagamento</span>
                        </NavLink>
                        <NavLink to="/app/provisions" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Landmark className="h-4 w-4 text-zinc-400" />
                          <span>Provisões</span>
                        </NavLink>
                        <NavLink to="/app/bank-reconciliation" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Landmark className="h-4 w-4 text-zinc-400" />
                          <span>Conciliação bancária</span>
                        </NavLink>
                        <NavLink to="/app/suppliers" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Building className="h-4 w-4 text-zinc-400" />
                          <span>Fornecedores</span>
                        </NavLink>
                      </>
                    )}
                  </>
                )}

                {/* 🍕 CARDÁPIO & VOUCHERS */}
                {access.canUseTeamModules && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">🍕 Cardápio & Vouchers</div>
                    <NavLink to="/app/menu" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <UtensilsCrossed className="h-4 w-4 text-zinc-400" />
                      <span>Cardápio</span>
                    </NavLink>
                    <NavLink to="/app/product-catalog" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <Package className="h-4 w-4 text-zinc-400" />
                      <span>Catálogo de produtos</span>
                    </NavLink>
                    {vouchersEnabled && (
                      <NavLink to="/app/vouchers" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Ticket className="h-4 w-4 text-zinc-400" />
                        <span>Vouchers</span>
                      </NavLink>
                    )}
                    {access.isAdmin && (
                      <NavLink to="/app/empresas" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Handshake className="h-4 w-4 text-zinc-400" />
                        <span>Empresas e papéis</span>
                      </NavLink>
                    )}
                  </>
                )}

                {canReviewCancellations && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">📞 Pós-venda</div>
                    <NavLink to="/app/pos-venda/cancelamentos" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <ClipboardCheck className="h-4 w-4 text-zinc-400" />
                      <span>Cancelamentos para revisar</span>
                    </NavLink>
                  </>
                )}

                {/* 💬 CLIENTES */}
                {(access.canUseTeamModules || (wifiEnabled && access.canViewDashboard)) && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">💬 Clientes</div>
                    <NavLink to="/app/crm/inbox" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                      <MessageSquare className="h-4 w-4 text-zinc-400" />
                      <span>CRM Inbox</span>
                    </NavLink>
                    {wifiEnabled && (
                      <NavLink to="/app/wifi/visitors" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Wifi className="h-4 w-4 text-zinc-400" />
                        <span>WiFi Analytics</span>
                      </NavLink>
                    )}
                  </>
                )}

                {/* ⚙️ SISTEMA */}
                {(access.canManageUsers || canUseSystemAdmin) && (
                  <>
                    <div className="mt-3 border-t border-white/10 px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">⚙️ Sistema</div>
                    {canUseSystemAdmin && (
                      <>
                        <NavLink to="/app/settings/units" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Store className="h-4 w-4 text-zinc-400" />
                          <span>Unidades</span>
                        </NavLink>
                        <NavLink to="/app/settings/parameters" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Sliders className="h-4 w-4 text-zinc-400" />
                          <span>Parâmetros</span>
                        </NavLink>
                        <NavLink to="/app/users" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <UserCog className="h-4 w-4 text-zinc-400" />
                          <span>Usuários &amp; acessos</span>
                        </NavLink>
                        <NavLink to="/app/settings/users" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <KeyRound className="h-4 w-4 text-zinc-400" />
                          <span>Senhas &amp; ativação (rede)</span>
                        </NavLink>
                        <NavLink to="/app/settings/groups" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Users2 className="h-4 w-4 text-zinc-400" />
                          <span>Grupos personalizados</span>
                        </NavLink>
                        <NavLink to="/app/auditoria" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <FileText className="h-4 w-4 text-zinc-400" />
                          <span>Auditoria</span>
                        </NavLink>
                        <NavLink to="/app/cofre" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Lock className="h-4 w-4 text-zinc-400" />
                          <span>Cofre de senhas</span>
                        </NavLink>
                        <NavLink to="/app/import" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                          <Upload className="h-4 w-4 text-zinc-400" />
                          <span>Importar dados</span>
                        </NavLink>
                      </>
                    )}
                    {hasAdminInAnyUnit && (
                      <NavLink to="/app/acesso/modulos" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Boxes className="h-4 w-4 text-zinc-400" />
                        <span>Módulos</span>
                      </NavLink>
                    )}
                    {access.canManageUsers && !canUseSystemAdmin && (
                      <NavLink to="/app/users" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                        <Shield className="h-4 w-4 text-zinc-400" />
                        <span>Usuários</span>
                      </NavLink>
                    )}
                  </>
                )}

                {manualEnabled && (
                  <NavLink to="/app/manual" onClick={() => setMobileMenuOpen(false)} className={navLinkClass}>
                    <BookOpen className="h-4 w-4 text-zinc-400" />
                    <span>Manual de uso</span>
                  </NavLink>
                )}
              </nav>

              <div className="border-t border-white/10 px-5 py-4">
                <div className="text-sm font-semibold text-white">{user?.displayName || user?.email || '—'}</div>
                <div className="mt-1 text-xs text-zinc-400">{selectedUnit?.name || 'Selecionar unidade'}</div>
              </div>
            </div>
          ) : null}

          <main className={`mx-auto w-full max-w-6xl px-4 py-6 text-foreground${showEmployeeBottomNav ? ' pb-20 md:pb-6' : ''}`}>
            <Outlet />
          </main>
          {showEmployeeBottomNav && <MobileBottomNav showDia />}
          {/* PWA: "Adicionar à tela inicial" — uma vez por aparelho, só no celular. */}
          <InstallPrompt />
        </div>
      </div>
    </div>
  )
}
